import { Component, OnInit, ViewChild } from "@angular/core";
import { DataService } from "../../services/data.service";
import { TagService } from "../../services/tag.service";
import { SearchService } from "../../services/search.service";
import Utils from "../../services/utils";
import { Router } from "@angular/router";
import * as _ from "underscore";
import { LoadingSpinnerService } from "../../components/loading-spinner/loading-spinner.service";
import { AuthService, UserPermissions } from "../../services/auth.service";
import { ToastService } from "../../components/toast/toast.service";
import { DropdownComponent } from "../../components/dropdown/dropdown.component";
import { MactelDiagnosis } from "../../models/adjudication.model";
import { MappingConsolidation } from "../../models/mapping-consolidation.model";
import { AddRemoveSearchColumnsComponent } from "./search-modals/add-remove-columns-modal.component";
import { TopLevelFieldMapping } from "../../models/top-level-field-mapping.model";
import { LowyGridComponent } from "../../components/grid/grid.component";
import { EditSearchTagsComponent } from "./search-modals/edit-search-tags-modal.component";
import { SelectPatientsComponent } from "./search-modals/select-patients-modal.component";
import { ResetFiltersModalComponent } from "./search-modals/reset-filters-modal.component";
import { MetaDataMapping } from "../../models/meta-data.model";
import { SelectImageColumnsComponent } from "./search-modals/select-image-columns-modal.component";
import { PageEvent, Sort } from "@angular/material";
import { SelectGeneticColumnsComponent } from "./search-modals/select-genetic-columns-modal.component";

@Component({
  templateUrl: "./search.component.html",
  styleUrls: ["./search.component.scss"],
  providers: [DataService, TagService, AuthService],
})
export class SearchComponent implements OnInit {
  @ViewChild("addRemoveColumnsModal")
  addRemoveColumnsModal: AddRemoveSearchColumnsComponent;
  @ViewChild("editSearchTagsModal")
  editSearchTagsModal: EditSearchTagsComponent;
  @ViewChild("selectPatientsModal")
  selectPatientsModal: SelectPatientsComponent;
  @ViewChild("resetFiltersModal")
  resetFiltersModal: ResetFiltersModalComponent;
  @ViewChild("addRemoveImageColumnsModal")
  addRemoveImageColumnsModal: SelectImageColumnsComponent;
  @ViewChild("addRemoveGeneticColumnsModal")
  addRemoveGeneticColumnsModal: SelectGeneticColumnsComponent;

  @ViewChild("patientGrid") patientGrid: LowyGridComponent;
  @ViewChild("imageGrid") imageGrid: LowyGridComponent;
  @ViewChild("geneticGrid") geneticGrid: LowyGridComponent;

  public topLevelGroups: TopLevelFieldMapping[];

  public userPermissions: UserPermissions;
  public mactelDiagnosis = MactelDiagnosis;
  public studies = { NHOS: "NHOS" };
  public selectedDataType = "patient";

  public alternateIds = {
    patId2: false,
    patId3: false,
    patId4: false,
  };
  // patient filters
  public patientCount = 0;
  public patientIds: string[] = [];
  public selectColumns: TopLevelFieldMapping[] = [];
  public patientDataFilters: {
    spreadsheetId: string;
    columnId: string;
    values: string[];
  }[] = [];
  // image filters
  public imageDataFilters: {
    columnId: string;
    values: string[];
  }[] = [];
  public imageCount = 0;
  public imageColumns: MetaDataMapping[];
  public selectedImageColumns: MetaDataMapping[];
  // image filters
  public geneticDataFilters: {
    columnId: string;
    values: string[];
  }[] = [];
  public geneticCount = 0;
  public geneticColumns: MetaDataMapping[];
  public selectedGeneticColumns: MetaDataMapping[];

  private gridStartIndex = 0;
  private gridRecordsPerPage = 10;
  public gridTypes = ["patient", "image", "genetic"];
  public gridSort: Sort;

  public patientTotal: number;

  constructor(
    private router: Router,
    private loadingSpinnerService: LoadingSpinnerService,
    private dataService: DataService,
    private tagService: TagService,
    private authService: AuthService,
    private toastService: ToastService,
    private searchService: SearchService
  ) {}

  async ngOnInit() {
    this.loadingSpinnerService.start();

    this.initFilters();
    this.authService.permissions$.subscribe((permissions) => {
      this.userPermissions = permissions;
    });

    this.topLevelGroups = await this.searchService.getTopLevelGroups();
    await this.buildDataGrid();

    this.imageColumns = await this.searchService.getImageMappings();
    this.geneticColumns = await this.searchService.getGeneticMappings();

    this.loadingSpinnerService.stop();
  }

  public selectDataType(dataType: string) {
    this.selectedDataType = dataType;
    this.gridStartIndex = 0;
    this.gridRecordsPerPage = 10;
    this.buildDataGrid();
  }

  public initFilters() {
    this.patientCount = 0;
    this.patientIds = [];
    this.patientDataFilters = [];
    this.selectColumns = [];

    this.selectedImageColumns = [];
    this.imageDataFilters = [];
    this.imageCount = 0;

    this.selectedGeneticColumns = [];
    this.geneticDataFilters = [];
    this.geneticCount = 0;

    if (this.patientGrid) {
      this.patientGrid.clearAllFilters();
    }
    if (this.imageGrid) {
      this.imageGrid.clearAllFilters();
    }
    if (this.geneticGrid) {
      this.geneticGrid.clearAllFilters();
    }
    if (this.addRemoveImageColumnsModal) {
      this.addRemoveImageColumnsModal.clearAll();
    }
    if (this.addRemoveGeneticColumnsModal) {
      this.addRemoveGeneticColumnsModal.clearAll();
    }
  }

  public showResetFiltersModal() {
    const subscription = this.resetFiltersModal.doReset.subscribe(() => {
      subscription.unsubscribe();
      this.initFilters();
      this.buildDataGrid();
    });
    this.resetFiltersModal.open();
  }

  public addEditColumns() {
    this.addRemoveColumnsModal.open(this.selectColumns);
  }

  public addEditImageColumns() {
    this.addRemoveImageColumnsModal.open(this.imageColumns);
  }

  public addEditGeneticColumns() {
    this.addRemoveGeneticColumnsModal.open(this.geneticColumns);
  }

  // public editSearchTags() {
  //   const subscription =
  //     this.editSearchTagsModal.saveTagsEventEmitter.subscribe(() => {
  //       subscription.unsubscribe();
  //     });
  //   this.editSearchTagsModal.open();
  // }

  public editPatientIds() {
    const subscription =
      this.selectPatientsModal.savePatientIdsEventEmitter.subscribe(
        ({ patientIds, alternateIds }) => {
          this.patientIds = patientIds;
          this.alternateIds = alternateIds;
          this.resetGridPagination();
          this.buildDataGrid();
        }
      );
    this.selectPatientsModal.open(this.patientIds);
  }

  public setColumns(columns: TopLevelFieldMapping[]) {
    this.selectColumns = columns;
    // if we remove a column that is being filtered on, remove the filter
    this.patientDataFilters = _.filter(this.patientDataFilters, (f) =>
      _.contains(
        _.pluck(this.selectColumns, "topLevelSpreadsheetId"),
        f.spreadsheetId
      )
    );
    this.buildDataGrid();
  }

  public filtersUpdated(
    filters: { spreadsheetId: string; columnId: string; values: string[] }[]
  ) {
    switch (this.selectedDataType) {
      case "patient":
        this.patientFiltersUpdated(filters);
        break;
      case "image":
        this.imageFiltersUpdated(filters);
        break;
      case "genetic":
        this.geneticFiltersUpdated(filters);
        break;
    }
  }

  public patientFiltersUpdated(
    filters: { spreadsheetId: string; columnId: string; values: string[] }[]
  ) {
    this.patientDataFilters = filters;
    this.resetGridPagination();
    this.buildDataGrid();
  }

  public imageFiltersUpdated(
    filter: { spreadsheetId: string; columnId: string; values: string[] }[]
  ) {
    this.imageDataFilters = _.map(filter, (f) => {
      f.columnId = f.spreadsheetId;
      delete f.spreadsheetId;
      return f;
    });
    this.resetGridPagination();
    this.buildDataGrid();
  }

  public geneticFiltersUpdated(
    filter: { spreadsheetId: string; columnId: string; values: string[] }[]
  ) {
    this.geneticDataFilters = _.map(filter, (f) => {
      f.columnId = f.spreadsheetId;
      delete f.spreadsheetId;
      return f;
    });
    this.resetGridPagination();
    this.buildDataGrid();
  }

  public async buildDataGrid() {
    this.loadingSpinnerService.start();
    let columns: string[];

    let showEye = false;
    const uniqSpreadsheetIds = _.pluck(
      this.selectColumns,
      "topLevelSpreadsheetId"
    );
    for (let i = 0; i < uniqSpreadsheetIds.length; i++) {
      if (
        [
          "CF-FFA",
          "FAFGRADE",
          "OCTMAIN",
          "OCTMTCIRRUS",
          "OCTMTSTRATUS",
          "OCTMTSPECTRALIS",
        ].includes(uniqSpreadsheetIds[i])
      ) {
        showEye = true;
        break;
      }
    }

    switch (this.selectedDataType) {
      case "patient":
        const alternateIds = [];
        if (this.alternateIds.patId2) alternateIds.push("patId2");
        if (this.alternateIds.patId3) alternateIds.push("patId3");
        if (this.alternateIds.patId4) alternateIds.push("patId4");
        columns = ["checkbox", "patientId"]
          .concat(alternateIds)
          .concat(["visitId"])
          .concat(showEye ? ["eye"] : [])
          .concat(
            _.map(
              this.selectColumns,
              (column) =>
                column.topLevelSpreadsheetId + ":" + column.topLevelColumnId
            )
          );
        break;
      case "image":
        columns = ["checkbox", "patientId", "visitId", "name"].concat(
          _.map(this.selectedImageColumns, (column) => column.columnId)
        );
        break;
      case "genetic":
        columns = ["checkbox", "patientId"].concat(
          _.map(this.selectedGeneticColumns, (column) => column.columnId)
        );
        break;
    }
    // column headers
    let columnHeaders: string[];
    switch (this.selectedDataType) {
      case "patient":
        const alternateIdNames = [];
        if (this.alternateIds.patId2) alternateIdNames.push("A Number");
        if (this.alternateIds.patId3) alternateIdNames.push("Patient ID 3");
        if (this.alternateIds.patId4) alternateIdNames.push("Patient ID 3");
        columnHeaders = ["checkbox", "Patient ID"]
          .concat(alternateIdNames)
          .concat(["Visit ID"])
          .concat(showEye ? ["Eye"] : [])
          .concat(
            _.map(
              this.selectColumns,
              (column) =>
                column.topLevelSpreadsheetId + ": " + column.topLevelColumnName
            )
          );
        break;
      case "image":
        columnHeaders = ["checkbox", "Patient ID", "Visit ID", "Name"].concat(
          _.map(this.selectedImageColumns, (column) => column.columnName)
        );
        break;
      case "genetic":
        columnHeaders = ["checkbox", "Patient ID"].concat(
          _.map(this.selectedGeneticColumns, (column) => column.columnName)
        );
        break;
    }
    // get data and build grid
    try {
      let gridCols;
      switch (this.selectedDataType) {
        case "patient":
          gridCols = this.selectColumns;
          break;
        case "image":
          gridCols = this.selectedImageColumns;
          break;
        case "genetic":
          gridCols = this.selectedGeneticColumns;
          break;
      }

      let data = await this.searchService.getLookupData({
        type: this.selectedDataType,
        start: this.gridStartIndex,
        count: this.gridRecordsPerPage,
        columns: gridCols,
        patientIds: this.patientIds,
        patientFilters: this.patientDataFilters,
        imageFilters: this.imageDataFilters,
        geneticFilters: this.geneticDataFilters,
        orderBy: this.gridSort ? this.gridSort.active : null,
        desc: this.gridSort ? this.gridSort.direction === "desc" : null,
      });
      const rows = data.rows;
      const filterValues = data.filterValues;
      this.patientTotal = data.patientTotal;
      switch (this.selectedDataType) {
        case "patient":
          this.patientCount = data.pagination.total;
          if (this.patientGrid) {
            this.patientGrid.dataReady(
              rows,
              columns,
              columnHeaders,
              filterValues,
              this.patientCount
            );
          }
          break;
        case "image":
          this.imageCount = data.pagination.total;
          if (this.imageGrid) {
            this.imageGrid.dataReady(
              rows,
              columns,
              columnHeaders,
              filterValues,
              this.imageCount
            );
          }
          break;
        case "genetic":
          this.geneticCount = data.pagination.total;
          if (this.geneticGrid) {
            this.geneticGrid.dataReady(
              rows,
              columns,
              columnHeaders,
              filterValues,
              this.geneticCount
            );
          }
          break;
      }
      this.loadingSpinnerService.stop();

      // do getters for other grids, for count totals
      switch (this.selectedDataType) {
        case "patient":
          this.updateImageTotal();
          this.updateGeneticTotal();
          break;
        case "image":
          this.updatePatientTotal();
          this.updateGeneticTotal();
          break;
        case "genetic":
          this.updateImageTotal();
          this.updatePatientTotal();
          break;
      }
    } catch (e) {
      this.toastService.throwToast("Error retrieving data: " + e.message);
      this.loadingSpinnerService.stop();
    }
  }

  private async updatePatientTotal() {
    let patientData = await this.searchService.getLookupData({
      type: "patient",
      start: 0,
      count: 10,
      columns: [],
      patientIds: this.patientIds,
      patientFilters: this.patientDataFilters,
      imageFilters: this.imageDataFilters,
      geneticFilters: this.geneticDataFilters,
    });
    this.patientCount = patientData.pagination.total;
  }
  private async updateImageTotal() {
    let imageData = await this.searchService.getLookupData({
      type: "image",
      start: 0,
      count: 10,
      columns: [],
      patientIds: this.patientIds,
      patientFilters: this.patientDataFilters,
      imageFilters: this.imageDataFilters,
      geneticFilters: this.geneticDataFilters,
    });
    this.imageCount = imageData.pagination.total;
  }
  private async updateGeneticTotal() {
    let geneticData = await this.searchService.getLookupData({
      type: "genetic",
      start: 0,
      count: 10,
      columns: [],
      patientIds: this.patientIds,
      patientFilters: this.patientDataFilters,
      imageFilters: this.imageDataFilters,
      geneticFilters: this.geneticDataFilters,
    });
    this.geneticCount = geneticData.pagination.total;
  }

  public setImageColumns(selectedColumns: MetaDataMapping[]) {
    this.selectedImageColumns = selectedColumns;
    // if we remove a column that is being filtered on, remove the filter
    this.imageDataFilters = _.filter(this.imageDataFilters, (f) =>
      _.contains(_.pluck(this.selectedImageColumns, "columnId"), f.columnId)
    );
    this.buildDataGrid();
  }

  public setGeneticColumns(selectedColumns: MetaDataMapping[]) {
    this.selectedGeneticColumns = selectedColumns;
    // if we remove a column that is being filtered on, remove the filter
    this.geneticDataFilters = _.filter(this.geneticDataFilters, (f) =>
      _.contains(_.pluck(this.selectedGeneticColumns, "columnId"), f.columnId)
    );
    this.buildDataGrid();
  }

  public removeGeneticFilter(filter: { columnId: string; values: string[] }) {
    this.geneticDataFilters = _.filter(
      this.geneticDataFilters,
      (f) => f.columnId !== filter.columnId
    );
    if (this.geneticGrid) {
      this.geneticGrid.clearFilter(filter);
    }
    this.resetGridPagination();
    this.buildDataGrid();
  }

  public removeImageFilter(filter: { columnId: string; values: string[] }) {
    this.imageDataFilters = _.filter(
      this.imageDataFilters,
      (f) => f.columnId !== filter.columnId
    );
    if (this.imageGrid) {
      this.imageGrid.clearFilter(filter);
    }
    this.resetGridPagination();
    this.buildDataGrid();
  }

  public removePatientFilter(filter: {
    spreadsheetId: string;
    columnId: string;
    values: string[];
  }) {
    this.patientDataFilters = _.filter(
      this.patientDataFilters,
      (f) =>
        !(
          f.columnId == filter.columnId &&
          f.spreadsheetId == filter.spreadsheetId
        )
    );
    if (this.patientGrid) {
      this.patientGrid.clearFilter(filter);
    }
    this.resetGridPagination();
    this.buildDataGrid();
  }

  public async pageChange(datatype: string, page: PageEvent) {
    this.gridStartIndex = page.pageIndex * page.pageSize;
    this.gridRecordsPerPage = page.pageSize;

    void this.buildDataGrid();
  }

  public async sortUpdated(sort: Sort) {
    this.gridSort = sort;
    void this.buildDataGrid();
  }

  public async downloadAllRecords(downloadType: string) {
    let gridCols, totalRowCount;
    switch (this.selectedDataType) {
      case "patient":
        gridCols = this.selectColumns;
        totalRowCount = this.patientGrid.totalRowCount;
        break;
      case "image":
        gridCols = this.selectedImageColumns;
        totalRowCount = this.imageGrid.totalRowCount;
        break;
      case "genetic":
        gridCols = this.selectedGeneticColumns;
        totalRowCount = this.geneticGrid.totalRowCount;
        break;
    }

    let allItems = await this.searchService.getLookupData({
      type: this.selectedDataType,
      start: 0,
      count: totalRowCount,
      columns: gridCols,
      patientIds: this.patientIds,
      patientFilters: this.patientDataFilters,
      imageFilters: this.imageDataFilters,
      geneticFilters: this.geneticDataFilters,
      orderBy: this.gridSort ? this.gridSort.active : null,
      desc: this.gridSort ? this.gridSort.direction === "desc" : null,
    });

    switch (this.selectedDataType) {
      case "patient":
        this.patientGrid.downloadFiles(downloadType, allItems.rows);
        break;
      case "image":
        this.imageGrid.downloadFiles(downloadType, allItems.rows);
        break;
      case "genetic":
        this.geneticGrid.downloadFiles(downloadType, allItems.rows);
        break;
    }
  }

  resetGridPagination() {
    this.gridStartIndex = 0;
    this.gridRecordsPerPage = 10;

    if (this.patientGrid) {
      this.patientGrid.resetPagination();
    }
    if (this.imageGrid) {
      this.imageGrid.resetPagination();
    }
    if (this.geneticGrid) {
      this.geneticGrid.resetPagination();
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/home/search/search.component.ts